import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDownRight,
  Filter,
  RefreshCcw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table";
import Badge from "../ui/badge/Badge";
import {
  CryptoTransaction,
  CryptoTxStatus,
  CryptoNetworkCode,
  CryptoToken,
  CryptoTxType,
  getNetworkInfo,
} from "./types";
import { TokenIcon, NetworkIcon } from "./CryptoIcons";
import { useModal } from "../../hooks/useModal";
import { listCryptoTransactions } from "./API/services";
import Spinner from "../ui/Spinner/Spinner";
import PageMeta from "../common/PageMeta";
import PageBreadCrumb from "../common/PageBreadCrumb";
import CryptoTransactionSummary from "./modals/CryptoTransactionSummary";

// NOTE: adjust these to match the actual enum values exported from ./types
// (NetworkType / TokenType / TransactionType on the backend Joi schema).
const NETWORK_OPTIONS: { value: CryptoNetworkCode | "ALL"; label: string }[] = [
  { value: "ALL", label: "All networks" },
  { value: "solana" as CryptoNetworkCode, label: "Solana" },
  { value: "ethereum" as CryptoNetworkCode, label: "Ethereum (ERC20)" },
  { value: "base" as CryptoNetworkCode, label: "Base" },
  { value: "tron" as CryptoNetworkCode, label: "Tron (Trc20)" },
  { value: "arbitrum" as CryptoNetworkCode, label: "Arbitrum" },
  { value: "polygon" as CryptoNetworkCode, label: "Polygon" },
];

const TOKEN_OPTIONS: { value: CryptoToken | "ALL"; label: string }[] = [
  { value: "ALL", label: "All tokens" },
  { value: "USDT" as CryptoToken, label: "USDT" },
  { value: "USDC" as CryptoToken, label: "USDC" },
];

const TYPE_OPTIONS: { value: CryptoTxType | "ALL"; label: string }[] = [
  { value: "ALL", label: "All types" },
  { value: "DEPOSIT" as CryptoTxType, label: "Deposit" },
  { value: "WITHDRAWAL" as CryptoTxType, label: "Withdrawal" },
  { value: "CHARGES" as CryptoTxType, label: "Charge" },
  //   { value: "REVERSAL" as CryptoTxType, label: "Reversal" },
];

const selectClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-theme-sm text-gray-700 shadow-theme-xs focus:border-primary focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300";

const dropdownTriggerClass =
  "flex w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-theme-sm text-gray-700 shadow-theme-xs focus:border-primary focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300";

const dropdownMenuClass =
  "absolute left-0 top-full z-20 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800";

const dropdownItemClass =
  "flex w-full items-center gap-2 px-3 py-2 text-left text-theme-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/[0.05]";

const CryptoTransactionsPage = () => {
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState<CryptoTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null);

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20;

  // --- filters ---
  const [showFilters, setShowFilters] = useState(false);
  const [networkFilter, setNetworkFilter] = useState<CryptoNetworkCode | "ALL">(
    "ALL",
  );
  const [tokenFilter, setTokenFilter] = useState<CryptoToken | "ALL">("ALL");
  const [typeFilter, setTypeFilter] = useState<CryptoTxType | "ALL">("ALL");

  const hasActiveFilters =
    networkFilter !== "ALL" || tokenFilter !== "ALL" || typeFilter !== "ALL";

  const [networkDropdownOpen, setNetworkDropdownOpen] = useState(false);
  const [tokenDropdownOpen, setTokenDropdownOpen] = useState(false);
  const networkDropdownRef = useRef<HTMLDivElement>(null);
  const tokenDropdownRef = useRef<HTMLDivElement>(null);

  const {
    isOpen: isSummaryOpen,
    openModal: openSummaryModal,
    closeModal: closeSummaryModal,
  } = useModal();

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, networkFilter, tokenFilter, typeFilter]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        networkDropdownRef.current &&
        !networkDropdownRef.current.contains(event.target as Node)
      ) {
        setNetworkDropdownOpen(false);
      }
      if (
        tokenDropdownRef.current &&
        !tokenDropdownRef.current.contains(event.target as Node)
      ) {
        setTokenDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await listCryptoTransactions({
        page,
        limit,
        network: networkFilter === "ALL" ? undefined : networkFilter,
        tokenType: tokenFilter === "ALL" ? undefined : tokenFilter,
        type: typeFilter === "ALL" ? undefined : typeFilter,
      });
      setTransactions(response.data);
      setPageCount(response.pageCount);
      setTotalCount(response.totalCount);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to fetch transactions");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Any filter change should reset back to page 1; if we're already on
  // page 1 the useEffect above still refires because a filter value changed.
  const updateFilter = <T,>(setter: (v: T) => void, value: T) => {
    setter(value);
    setPage(1);
  };

  const clearFilters = () => {
    setNetworkFilter("ALL");
    setTokenFilter("ALL");
    setTypeFilter("ALL");
    setPage(1);
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const getStatusBadgeColor = (
    status: CryptoTxStatus,
  ): "success" | "warning" | "error" => {
    switch (status) {
      case "CONFIRMED":
        return "success";
      case "PENDING":
      case "FUNDING":
        return "warning";
      case "FAILED":
        return "error";
    }
  };

  const getStatusLabel = (status: CryptoTxStatus) => {
    switch (status) {
      case "FUNDING":
        return "PENDING";
      default:
        return status;
    }
  };
  // DEPOSIT and REVERSAL bring funds in; WITHDRAWAL and CHARGES take funds out
  const getAmountStyle = (transaction: CryptoTransaction) => {
    const isCredit =
      transaction.type === "DEPOSIT" || transaction.type === "REVERSAL";
    if (transaction.status === "FAILED") {
      return { sign: "-", colorClass: "text-red-600 dark:text-red-400" };
    }
    return isCredit
      ? { sign: "+", colorClass: "text-green-600 dark:text-green-400" }
      : { sign: "-", colorClass: "text-red-600 dark:text-red-400" };
  };

  const getTypeLabel = (type: CryptoTransaction["type"]) => {
    switch (type) {
      case "DEPOSIT":
        return "Deposit";
      case "WITHDRAWAL":
        return "Withdrawal";
      case "CHARGES":
        return "Charge";
      case "REVERSAL":
        return "Reversal";
      default:
        return type;
    }
  };

  const startEntry = totalCount === 0 ? 0 : (page - 1) * limit + 1;
  const endEntry = Math.min(page * limit, totalCount);

  return (
    <>
      <PageMeta
        title="Stable coin Transactions"
        description="View all transactions across your Stable coin Wallet"
      />
      <PageBreadCrumb pageTitle="Stable coin Transactions" />

      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        {/* Page header */}
        <div className="mb-6">
          <div className="mb-4 flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-400"
              aria-label="Go back"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            {/* <h1 className="text-xl font-semibold text-gray-800 dark:text-white/90">
              Stable coin Wallet Transactions
            </h1> */}
          </div>
        </div>

        {/* Table card */}
        <div className="overflow-hidden w-full rounded-2xl border border-gray-200 bg-white px-4 pb-3 pt-4 dark:border-gray-800 dark:bg-white/[0.03] sm:px-6">
          {/* Card header */}
          <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              All Transactions
            </h3>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-theme-sm font-medium shadow-theme-xs ${
                  hasActiveFilters
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
                }`}
              >
                <Filter className="h-4 w-4" />
                <span className="hidden sm:inline">Filter</span>
                {hasActiveFilters && (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-white">
                    {
                      [networkFilter, tokenFilter, typeFilter].filter(
                        (f) => f !== "ALL",
                      ).length
                    }
                  </span>
                )}
              </button>

              <button
                onClick={fetchTransactions}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-theme-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
              >
                <RefreshCcw className="h-4 w-4" />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>

          {/* Filter panel */}
          {showFilters && (
            <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-white/[0.02]">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="relative" ref={networkDropdownRef}>
                  <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                    Network
                  </label>
                  <button
                    type="button"
                    onClick={() => setNetworkDropdownOpen((o) => !o)}
                    className={dropdownTriggerClass}
                  >
                    <span className="flex items-center gap-2">
                      {networkFilter !== "ALL" && (
                        <NetworkIcon
                          network={networkFilter as CryptoNetworkCode}
                          size={16}
                        />
                      )}
                      {
                        NETWORK_OPTIONS.find(
                          (opt) => opt.value === networkFilter,
                        )?.label
                      }
                    </span>
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </button>

                  {networkDropdownOpen && (
                    <div className={dropdownMenuClass}>
                      {NETWORK_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            updateFilter(setNetworkFilter, opt.value);
                            setNetworkDropdownOpen(false);
                          }}
                          className={dropdownItemClass}
                        >
                          {opt.value !== "ALL" && (
                            <NetworkIcon
                              network={opt.value as CryptoNetworkCode}
                              size={16}
                            />
                          )}
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="relative" ref={tokenDropdownRef}>
                  <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                    Token
                  </label>
                  <button
                    type="button"
                    onClick={() => setTokenDropdownOpen((o) => !o)}
                    className={dropdownTriggerClass}
                  >
                    <span className="flex items-center gap-2">
                      {tokenFilter !== "ALL" && (
                        <TokenIcon
                          token={tokenFilter as CryptoToken}
                          size={16}
                        />
                      )}
                      {
                        TOKEN_OPTIONS.find((opt) => opt.value === tokenFilter)
                          ?.label
                      }
                    </span>
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </button>

                  {tokenDropdownOpen && (
                    <div className={dropdownMenuClass}>
                      {TOKEN_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            updateFilter(setTokenFilter, opt.value);
                            setTokenDropdownOpen(false);
                          }}
                          className={dropdownItemClass}
                        >
                          {opt.value !== "ALL" && (
                            <TokenIcon
                              token={opt.value as CryptoToken}
                              size={16}
                            />
                          )}
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                    Type
                  </label>
                  <select
                    className={selectClass}
                    value={typeFilter}
                    onChange={(e) =>
                      updateFilter(
                        setTypeFilter,
                        e.target.value as CryptoTxType | "ALL",
                      )
                    }
                  >
                    {TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="h-3 w-3" />
                  Clear filters
                </button>
              )}
            </div>
          )}

          {/* Scrollable table */}
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-360px)] min-h-[200px]">
            {loading ? (
              <div className="flex h-48 items-center justify-center">
                <Spinner className="h-10 w-10 text-primary" />
              </div>
            ) : error ? (
              <div className="flex h-48 flex-col items-center justify-center gap-4 text-center p-6">
                <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/30">
                  <ArrowDownRight className="h-6 w-6 text-red-500" />
                </div>
                <p className="text-gray-600 dark:text-gray-400">{error}</p>
                <button
                  onClick={fetchTransactions}
                  className="rounded-lg bg-primary px-4 py-2 text-sm text-white"
                >
                  Retry
                </button>
              </div>
            ) : transactions.length === 0 ? (
              <div className="flex h-48 flex-col items-center justify-center gap-3 text-center p-6">
                <div className="rounded-full bg-gray-100 p-3 dark:bg-gray-800">
                  <Filter className="h-6 w-6 text-gray-400" />
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  {hasActiveFilters
                    ? "No transactions match your filters"
                    : "No transactions found"}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {hasActiveFilters
                    ? "Try adjusting or clearing your filters"
                    : "After your first transaction, you will be able to view it here"}
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader className="border-gray-100 dark:border-gray-800 border-y sticky top-0 bg-white dark:bg-gray-900 z-10">
                  <TableRow>
                    <TableCell
                      isHeader
                      className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                    >
                      Type
                    </TableCell>
                    <TableCell
                      isHeader
                      className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                    >
                      Network
                    </TableCell>
                    <TableCell
                      isHeader
                      className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                    >
                      Amount
                    </TableCell>
                    <TableCell
                      isHeader
                      className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                    >
                      Date
                    </TableCell>
                    <TableCell
                      isHeader
                      className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                    >
                      Status
                    </TableCell>
                    <TableCell
                      isHeader
                      className="py-3 text-theme-xs text-gray-500 text-start dark:text-gray-400"
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHeader>

                <TableBody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {transactions.map((transaction) => {
                    const { sign, colorClass } = getAmountStyle(transaction);
                    return (
                      <TableRow key={transaction.id}>
                        <TableCell className="py-3 min-w-48 text-gray-800 text-theme-sm dark:text-white/90">
                          <div className="flex items-center gap-2">
                            <TokenIcon token={transaction.token} size={20} />
                            <div>
                              <p className="font-medium">
                                {getTypeLabel(transaction.type)} ·{" "}
                                {transaction.token}
                              </p>
                              {transaction.txHash && (
                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-48 font-mono">
                                  {transaction.txHash}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>

                        <TableCell className="py-3 min-w-28 text-gray-500 text-theme-sm dark:text-gray-400">
                          <div className="flex items-center gap-2">
                            <NetworkIcon
                              network={transaction.network}
                              size={18}
                            />
                            {getNetworkInfo(transaction.network).name}
                          </div>
                        </TableCell>

                        <TableCell
                          className={`py-3 min-w-32 text-theme-sm font-semibold ${colorClass}`}
                        >
                          {sign}{" "}
                          {transaction.amount.toLocaleString(undefined, {
                            maximumFractionDigits: 8,
                          })}{" "}
                          {transaction.token}
                          {transaction.fee > 0 && (
                            <p className="text-xs font-normal text-gray-400 dark:text-gray-500">
                              Fee: {transaction.fee} {transaction.token}
                            </p>
                          )}
                        </TableCell>

                        <TableCell className="py-3 min-w-40 text-gray-500 text-theme-sm dark:text-gray-400">
                          {formatDate(transaction.createdAt)}
                        </TableCell>

                        <TableCell className="py-3 pr-4">
                          <Badge
                            size="sm"
                            color={getStatusBadgeColor(transaction.status)}
                          >
                            {getStatusLabel(transaction.status)}
                          </Badge>
                        </TableCell>

                        <TableCell>
                          <button
                            onClick={() => {
                              setSelectedTransactionId(transaction.id);
                              openSummaryModal();
                            }}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-2 py-1 text-theme-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200"
                          >
                            View
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Footer: entry count + pagination */}
          {transactions.length > 0 && (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-gray-100 dark:border-gray-800">
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                Showing {startEntry}–{endEntry} of {totalCount} transactions
              </p>

              {pageCount > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {Array.from({ length: pageCount }, (_, i) => i + 1)
                    .filter(
                      (p) =>
                        p === 1 || p === pageCount || Math.abs(p - page) <= 1,
                    )
                    .reduce<(number | "…")[]>((acc, p, idx, arr) => {
                      if (idx > 0 && p - (arr[idx - 1] as number) > 1)
                        acc.push("…");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === "…" ? (
                        <span
                          key={`ellipsis-${idx}`}
                          className="flex h-8 w-8 items-center justify-center text-xs text-gray-400"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={item}
                          onClick={() => setPage(item as number)}
                          className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs font-medium transition-colors ${
                            page === item
                              ? "bg-primary text-white"
                              : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                          }`}
                        >
                          {item}
                        </button>
                      ),
                    )}

                  <button
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    disabled={page === pageCount}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <CryptoTransactionSummary
        isOpen={isSummaryOpen}
        onClose={closeSummaryModal}
        transactionId={selectedTransactionId}
      />
    </>
  );
};

export default CryptoTransactionsPage;
